import { useEffect, useState } from "react";
import { Loader } from "src/components/shared";
import {
  HeroComponent,
  MarqueeComponent,
  MarketingComponent,
  OurApproachComponent,
  CapabilityComponent,
  NewsComponent,
  StreamLineComponent,
  SuperChargingComponent,
} from "src/pages/Home/components";
import Layout from "src/pages/Layout/Layout";
import { client } from "src/utils/api";
import { ContentTileComponent } from "./components/contentTile";

const HomePage = () => {
  const gqlClient = client;
  const [homePageData, setHomePageData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [seoData, setSeoData] = useState<any>();

  useEffect(() => {
    const fetchHomePageBasicComponents = async (skip: number) => {
      return await gqlClient.GetHomePageBasicComponents({
        componentsLimit: 5,
        skip,
        internalName: "Home",
      });
    };

    const fetchHomePageListComponents = async (skip: number) => {
      return await gqlClient.GetHomePageListComponents({
        componentsLimit: 5,
        skip,
        articleListLimit: 4,
        internalName: "Home",
      });
    };

    const fetchHomePageNavComponents = async (skip: number) => {
      return await gqlClient.GetHomePageNavComponents({
        componentsLimit: 5,
        infoLimit: 5,
        skip,
        internalName: "Home",
      });
    };

    const fetchHomePageCardComponents = async (skip: number) => {
      return await gqlClient.GetHomePageCardComponents({
        componentsLimit: 5,
        accordionItemLimit: 3,
        skip,
        internalName: "Home",
      });
    };

    const fetchHomePageSEO = async () => {
      return await gqlClient.GetHomePageSEO({
        internalName: "Home",
      });
    };

    const fetchHomePageData = async () => {
      try {
        const pageSize = 5;
        let allData: any[] = [];
        const totalComponents = 15;

        // Fetch SEO data separately
        const seoData = await fetchHomePageSEO();
        
        const seoComponent =
          seoData?.pageGenericCollection?.items[0]?.componentsCollection?.items?.find(
            (item: any) => item.__typename === "SeoFields"
          );
        if (seoComponent) setSeoData(seoComponent);

        for (let skip = 0; skip < totalComponents; skip += pageSize) {
          // Fetch all parts in parallel
          const [basicData, listData, navData, cardData] = await Promise.all([
            fetchHomePageBasicComponents(skip),
            fetchHomePageListComponents(skip),
            fetchHomePageNavComponents(skip),
            fetchHomePageCardComponents(skip),
          ]);

          const basicComponents =
            basicData?.pageGenericCollection?.items[0]?.componentsCollection
              ?.items || [];
          const listComponents =
            listData?.pageGenericCollection?.items[0]?.componentsCollection
              ?.items || [];
          const navComponents =
            navData?.pageGenericCollection?.items[0]?.componentsCollection
              ?.items || [];
          const cardComponents =
            cardData?.pageGenericCollection?.items[0]?.componentsCollection
              ?.items || [];

          // Combine all components
          const components = [
            ...basicComponents,
            ...listComponents,
            ...navComponents,
            ...cardComponents,
          ];
          allData = [...allData, ...components];

          // Break if we have fewer items than requested
          if (components.length < pageSize) break;
        }

        // Remove duplicates based on sys.id
        const uniqueData = Array.from(
          new Map(
            allData
              .filter((component: any) => component?.sys?.id)
              .map((item) => [item.sys.id, item])
          ).values()
        );

        setHomePageData(uniqueData);
      } catch (error) {
        console.error("Error fetching homepage data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchHomePageData();
  }, []);



  const componentMap = {
    Hero: HeroComponent,
    ContentTile: ContentTileComponent,
    LogoList: {
      LogoList: MarqueeComponent,
    },
   
    TabList: {
      TabList: (props) => <OurApproachComponent {...props} isHomePage={true} className="home-tablist" />,
    },
    VerticalTabList: CapabilityComponent,
    Accordion: {
      Accordion: StreamLineComponent,
      Card: SuperChargingComponent,
    },
  };

  const renderComponent = (component: any) => {
    const contentTypeId = component.__typename;
    const variant = component.variant;

    if (
      contentTypeId === "Accordion" ||
      contentTypeId === "LogoList" ||
      contentTypeId === "TabList"
    ) {
      const SpecificComponent = componentMap[contentTypeId]?.[variant];
      if (SpecificComponent) {
        return (
          <SpecificComponent
            key={`${component.sys.id}-${component.__typename}-${variant}`}
            content={component}
          />
        );
      }
      console.warn(
        `Unhandled variant: ${variant} for contentTypeId: ${contentTypeId}`
      );
      return null;
    }

    const GenericComponent = componentMap[contentTypeId];
    if (GenericComponent) {
      return (
        <GenericComponent
          key={`${component.sys.id}-${component.__typename}`}
          content={component}
        />
      );
    }

    console.warn(`Unhandled component type: ${contentTypeId}`);
    return null;
  };

  return (
    <Layout isHome={true} loading={loading} seoData={seoData}>
      <main className="home-page">
        {loading ? (
          // <Loader /> 
          <></>
        ) : (
          homePageData.map((component) => renderComponent(component))
        )}
      </main>
    </Layout>
  );
};

export default HomePage;
